/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { Box, Flex, VStack } from 'chakra-v3';
import { Text } from '../components/Text';
import { Button } from '../components/buttons/Index';
import SamikshaHeader from './components/SamikshaHeader';
import ReadMoreBlock from './components/ReadMoreBlock';
import Pipeline from './components/Pipeline';
import BenchmarkDatasetPreview from './components/BenchmarkDatasetPreview';
import Leaderboard from './components/Leaderboard.tsx';
import Notes from './components/Notes';
import Methodology from './components/Methodology';
import WhatMakesSamikshaDistinct from './components/WhatMakesSamikshaDistinct';
import IntendedUse from './components/IntendedUse';
import InsightsAndRecommendations from './components/InsightsAndRecommendations';
import SamikshaFooter from './components/SamikshaFooter';
import { MdOutlineOpenInNew } from 'react-icons/md';

interface StatCardProps {
  label: string;
  width?: string | { base?: string; md?: string; lg?: string };
  animatedValue: number;
}

const StatCard: React.FC<StatCardProps> = ({ label, width, animatedValue }) => (
  <Box
    border="0.8px solid"
    borderColor="rgba(10, 49, 60, 0.1)"
    bg="transparent"
    borderRadius="4px"
    p={{ base: 3, sm: 4, md: 5, lg: 6 }}
    h={{ base: 'auto', md: 'auto', lg: '180px' }}
    minH={{ base: '120px', md: '140px', lg: '180px' }}
    display="flex"
    alignItems="center"
    justifyContent="center"
    w={width}
    flex={{ base: '1 0 0', md: '1 1 0', lg: 'none' }}
    flexShrink={0}
  >
    <VStack gap={2} alignItems="center" w="100%">
      <Text
        fontFamily="heading"
        fontSize={{ base: '36px', sm: '48px', md: '52px', lg: '60px' }}
        fontWeight="normal"
        lineHeight="1.1"
        letterSpacing="-1px"
        color="#00afdf"
        whiteSpace="pre-wrap"
      >
        {Math.floor(animatedValue)}
      </Text>
      <Text
        fontFamily="body"
        fontSize={{ base: '14px', sm: '18px', md: '20px', lg: '24px' }}
        fontWeight="normal"
        lineHeight="normal"
        color="#f6f6f6"
      >
        {label}
      </Text>
    </VStack>
  </Box>
);

const Samiksha: React.FC = () => {
  const [modelsCount, setModelsCount] = useState(0);
  const [languagesCount, setLanguagesCount] = useState(0);
  const [domainsCount, setDomainsCount] = useState(0);
  const reportPdfPublicPath = `${import.meta.env.BASE_URL}reports/SamikshaReport.pdf`;

  const openReportModal = () => {
    window.open(reportPdfPublicPath, '_blank', 'noopener,noreferrer');
  };

  const scrollToLeaderboard = () => {
    const element = document.getElementById('leaderboard');
    if (element) {
      const header = document.querySelector('nav[data-sticky-header]') as HTMLElement;
      const headerHeight = header ? header.offsetHeight : 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const duration = 1800; // 1.8 seconds for a more graceful count-up
    const animationFrameIds: Set<number> = new Set();
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];

    // Ease-in-out quart: smooth acceleration and deceleration
    const easeInOutQuart = (t: number): number =>
      t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;

    const animateValue = (
      start: number,
      end: number,
      setter: (value: number) => void,
      delay: number = 0
    ) => {
      const timeoutId = setTimeout(() => {
        const startTime = performance.now();

        const animate = () => {
          const currentTime = performance.now();
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);

          const currentValue = start + (end - start) * easeInOutQuart(progress);
          setter(currentValue);

          if (progress < 1) {
            const rafId = requestAnimationFrame(animate);
            animationFrameIds.add(rafId);
          } else {
            setter(end);
          }
        };

        const rafId = requestAnimationFrame(animate);
        animationFrameIds.add(rafId);
      }, delay);

      timeoutIds.push(timeoutId);
    };

    animateValue(0, 17, setModelsCount, 0);
    animateValue(0, 11, setLanguagesCount, 80);
    animateValue(0, 4, setDomainsCount, 160);

    // Cleanup function
    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
      animationFrameIds.forEach((id) => cancelAnimationFrame(id));
    };
  }, []);

  return (
    <Box minH="80vh" bg="#0A313C" position="relative">
      <SamikshaHeader />

      <Box
        w="100%"
        position="relative"
        bg="radial-gradient(154.71% 100% at 50% 0%, #022A35 0%, #0D4B56 100%)"
      >
        {/* Hero Section */}
        <Box
          position="relative"
          py={{ base: 8, md: 12 }}
          px={{ base: 4, md: 6 }}
          maxW="1440px"
          mx="auto"
          w="100%"
        >
          <VStack
            gap="24px"
            alignItems="center"
            justifyContent="center"
            maxW="1099px"
            mx="auto"
            w="100%"
            position="relative"
            top={{ base: '12px', md: '28px', lg: '80px' }}
          >
            {/* Main Heading */}
            <Text
              fontFamily="heading"
              fontSize={{ base: '32px', sm: '40px', md: '50px', lg: '60px' }}
              fontWeight="300"
              lineHeight={{ base: '1.33', lg: '80px' }}
              letterSpacing="normal"
              color="#f9f9f9"
              textAlign="center"
              maxW="100%"
              px={{ base: 2, md: 0, lg: 0 }}
              whiteSpace="pre-wrap"
            >
              Samiksha is a pipeline for LLM Evaluation across 11 Indian Languages, grounded in real lives
            </Text>
          </VStack>

          {/* View Leaderboard Button */}
          <Flex
            justify="center"
            align="center"
            position="relative"
            top={{ base: '28px', md: '48px', lg: '140px' }}
            mt={4}
            gap={{ base: 3, md: '20px' }}
            direction={{ base: 'column', sm: 'row' }}
          >
            <Button
              bg="#055369"
              border="0.8px solid"
              borderColor="rgba(10, 49, 60, 0.1)"
              color="#f6f6f6"
              fontFamily="body"
              fontSize={{ base: '14px', sm: '15px', md: '16px', lg: '16px' }}
              fontWeight="600"
              lineHeight="1.4"
              letterSpacing="0.08px"
              h={{ base: '44px', md: '48px', lg: '48px' }}
              w={{ base: '180px', sm: '195px', md: '210px', lg: '210px' }}
              borderRadius="4px"
              px={2}
              py={2}
              _hover={{
                bg: '#044354',
              }}
              onClick={scrollToLeaderboard}
            >
              View Leaderboard
            </Button>
            <Button
              bg="transparent"
              border="0.8px solid"
              borderColor="#F9F9F9"
              color="#F9F9F9"
              fontFamily="body"
              fontSize="16px"
              fontWeight="600"
              lineHeight="1.4"
              letterSpacing="0.08px"
              h={{ base: '44px', md: '48px', lg: '48px' }}
              w={{ base: '180px', sm: '195px', md: '210px', lg: '210px' }}
              borderRadius="4px"
              p="8px"
              gap="12px"
              _hover={{
                bg: 'rgba(249, 249, 249, 0.08)',
              }}
              onClick={openReportModal}
            >
              View Report
              <MdOutlineOpenInNew size={24} color="#F9F9F9" />
            </Button>
          </Flex>

          {/* Statistics Cards */}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 6, md: 6, lg: 10 }}
            alignItems="stretch"
            justifyContent="center"
            maxW="1040px"
            mx="auto"
            w="100%"
            position="relative"
            top={{ base: '60px', md: '100px', lg: '220px' }}
            px={{ base: 4, md: 6, lg: 0 }}
            mb={{ base: '120px', md: '140px', lg: '220px' }}
            flexWrap={{ base: 'nowrap', md: 'wrap', lg: 'nowrap' }}
          >
            <StatCard
              label="AI Models Evaluated"
              width={{ base: '100%', md: 'calc(50% - 12px)', lg: 'auto' }}
              animatedValue={modelsCount}
            />
            <StatCard
              label="Indian Languages"
              width={{ base: '100%', md: 'calc(50% - 12px)', lg: '320px' }}
              animatedValue={languagesCount}
            />
            <StatCard
              label="Critical Domains"
              width={{ base: '100%', md: 'calc(50% - 12px)', lg: '308px' }}
              animatedValue={domainsCount}
            />
          </Flex>
        </Box>

        {/* Content Blocks Section */}
        <Box w="100%" position="relative">
          <Box
            py={{ base: 8, md: 12, lg: 12 }}
            px={{ base: 4, md: 6, lg: 6 }}
            maxW="1440px"
            mx="auto"
            w="100%"
          >
            <Box
              border="0.8px solid"
              borderColor="#055369"
              borderRadius="10px"
              bg="radial-gradient(154.71% 100% at 50% 0%, #022A35 0%, #0D4B56 100%)"
              p="32px"
              maxW="1185px"
              mx="auto"
              w="100%"
            >
              <VStack gap="24px" alignItems="stretch" w="100%">
                <ReadMoreBlock
                  title="What Samiksha Is"
                  introText="Samiksha is a multilingual benchmark that tests AI models on real, everyday Indian contexts to surface where they fall short in being helpful to lived realities."
                  expandedContent={{
                    leftColumn: `Few opportunities are as daunting—or as revealing—as India when it comes to building technology at scale. It is a population at genuine scale: dozens of languages, shifting cultural logics, and lived realities that sit far outside the data used to train most frontier AI systems. Today's LLMs are built on datasets dominated by the English-speaking Global North, leaving out the cultural reasoning, linguistic nuance, and everyday experience that shape decision-making for large segments of India's population.`,
                    rightColumn: `Samiksha is designed to make these omissions visible. It tests models on the real questions that the economically, linguistically, and socially diverse population of India would ask on a day-to-day basis. Members of the population help shape and validate these questions, grounding the benchmark in lived realities rather than abstract categories. The same people who are navigating digital payment glitches or responding to a child's fever now help judge whether a chatbot can meaningfully answer questions about finance, health, or daily life in their own language. Samiksha shows what it would take for AI systems to work for the full diversity of India's users.`,
                  }}
                  defaultExpanded={false}
                />

                {/* Divider Line */}
                <Box h="0" w="100%" position="relative" flexShrink={0}>
                  <Box
                    position="absolute"
                    inset="0"
                    borderTop="0.8px solid"
                    borderColor="#055369"
                  />
                </Box>

                <ReadMoreBlock
                  title="Why Samiksha Matters"
                  introText="People across India are beginning to use AI systems for guidance in areas that shape daily life. These systems must understand how users frame their concerns, recognise local context, and offer responses that are useful, trustworthy, and culturally aligned."
                  expandedContent={{
                    leftColumn: `It is still unclear how consistently today's language models meet this standard. Their training rarely reflects the linguistic patterns, cultural cues, and everyday situations that influence how decisions are made across India's diverse population.`,
                    rightColumn: `Samiksha evaluates a range of conversational models (starting with an initial 5-model leaderboard) across eleven languages and key domains to clarify this. By examining how models respond to real questions posed by members of the population, it reveals whether their outputs are applicable in practice and where improvements are needed for AI to become a reliable tool for everyday problem-solving.`,
                  }}
                  defaultExpanded={false}
                />
              </VStack>
            </Box>
          </Box>

          {/* Gap after content blocks */}
          <Box h="111px" />
        </Box>
      </Box>

      {/* Key Takeaways Section */}
      <Box id="insights-and-recommendations" w="100%" style={{ scrollMarginTop: '100px' }}>
        <InsightsAndRecommendations />
      </Box>

      {/* Pipeline Section */}
      <Box id="pipeline" w="100%" bg="#F7F3EF">
        <Box maxW="1440px" mx="auto" w="100%">
          <Pipeline />
        </Box>
      </Box>

      {/* Gradient Background Section - Benchmark Dataset Preview through Leaderboard */}
      <Box position="relative" w="100%" overflow="hidden">
        {/* Blurred Gradient Background - Full Width */}
        <Box
          position="absolute"
          left="50%"
          top="0"
          transform="translateX(-50%)"
          w="100vw"
          h="100%"
          minH="592px"
          bg="radial-gradient(154.71% 100% at 50% 0%, #0D4B56 0%, #022A35 100%)"
          pointerEvents="none"
          zIndex={0}
        />

        {/* Content - sits above gradient */}
        <Box
          position="relative"
          zIndex={1}
          display="flex"
          w="100%"
          maxW="1440px"
          mx="auto"
          flexDirection="column"
          alignItems="flex-start"
        >
          {/* Benchmark Dataset Preview Section */}
          <Box w="100%">
            <BenchmarkDatasetPreview />
          </Box>

          {/* Leaderboard Section */}
          <Box w="100%">
            <Leaderboard onOpenReport={openReportModal} />
          </Box>
        </Box>
      </Box>
      {/* Notes Section */}
      <Box id="notes" w="100%" bg="#f7f3ef">
        <Box maxW="1440px" mx="auto" w="100%">
          <Notes />
        </Box>
      </Box>

      {/* Methodology Section */}
      <Box id="methodology" w="100%" bg="#f7f3ef" style={{ scrollMarginTop: '100px' }}>
        <Box maxW="1440px" mx="auto" w="100%">
          <Methodology />
        </Box>
      </Box>

      {/* What Makes Samiksha Distinct Section */}
      <Box w="100%" bg="white">
        <Box maxW="100%" mx="auto" w="100%">
          <WhatMakesSamikshaDistinct />
        </Box>
      </Box>

      {/* Intended Use Section */}
      <Box w="100%" bg="white">
        <Box maxW="100%" mx="auto" w="100%">
          <IntendedUse />
        </Box>
      </Box>

      {/* Footer Section */}
      <SamikshaFooter />
    </Box>
  );
};

export default Samiksha;
